import React, { useEffect, useRef } from "react";

// Effet "Dither" (façon reactbits.dev), réécrit en interne en Canvas 2D. Le tramage ordonné
// (Bayer 4×4) est la vraie technique des vieux écrans à faible profondeur de couleur : chaque
// pixel est comparé à un seuil de cette matrice pour décider noir ou blanc — ça donne un grain
// artisanal plutôt qu'un dégradé lisse, et c'est intrinsèquement monochrome (le dithering réduit
// justement une image à deux tons), donc rien à désaturer ici.
const BAYER_4X4 = [
  [0, 8, 2, 10],
  [12, 4, 14, 6],
  [3, 11, 1, 9],
  [15, 7, 13, 5],
].map(row => row.map(v => v / 16));

// resolution : largeur interne du canvas en pixels, volontairement basse (quelques dizaines) et
// agrandie ensuite en CSS avec image-rendering:pixelated — c'est ce qui donne le grain "blocky"
// caractéristique du dithering plutôt qu'un flou lisse, et garde le coût par frame négligeable
// (quelques milliers de pixels, pas les millions d'un canvas plein écran).
// opacity : opacité maximale des pixels "allumés" (blancs) — pensé comme un voile de grain
// au-dessus d'un autre fond (Silk par exemple), pas comme un fond plein en soi.
export default function Dither({
  resolution = 110,
  opacity = 0.5,
  className,
  style,
}: {
  resolution?: number;
  opacity?: number;
  className?: string;
  style?: React.CSSProperties;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const parent = canvas?.parentElement;
    if (!canvas || !parent) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let raf = 0;
    let cw = 1, ch = 1;

    const resize = () => {
      const rect = parent.getBoundingClientRect();
      const ratio = rect.height / Math.max(1, rect.width);
      cw = resolution;
      ch = Math.max(1, Math.round(resolution * ratio));
      canvas.width = cw;
      canvas.height = ch;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(parent);

    const draw = (t: number) => {
      const img = ctx.createImageData(cw, ch);
      const time = t * 0.00025;
      for (let y = 0; y < ch; y++) {
        for (let x = 0; x < cw; x++) {
          // Trois sinusoïdes décalées font une texture de bruit lisse et animée, sans dépendance
          // externe (pas besoin d'un vrai bruit de Perlin pour un simple grain qui respire).
          const nx = x / cw, ny = y / ch;
          const n =
            Math.sin(nx * 8 + time * 2) +
            Math.sin(ny * 7 - time * 1.6) +
            Math.sin((nx + ny) * 6 + time * 2.4);
          const v = (n + 3) / 6; // normalisé 0..1
          const threshold = BAYER_4X4[y % 4][x % 4];
          const on = v > threshold;
          const idx = (y * cw + x) * 4;
          img.data[idx] = 255;
          img.data[idx + 1] = 255;
          img.data[idx + 2] = 255;
          img.data[idx + 3] = on ? Math.round(opacity * 255) : 0;
        }
      }
      ctx.putImageData(img, 0, 0);
      if (!reduceMotion) raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [resolution, opacity]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className={className}
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block", imageRendering: "pixelated", ...style } as React.CSSProperties}
    />
  );
}
